'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { apiFetch } from '@/lib/api';

import { Web3AuthNoModal } from "@web3auth/no-modal";
import { EthereumPrivateKeyProvider } from "@web3auth/ethereum-provider";
import {
    CHAIN_NAMESPACES,
    IProvider,
    WALLET_ADAPTERS,
    WEB3AUTH_NETWORK,
    ADAPTER_STATUS
} from "@web3auth/base";
import { AuthAdapter } from "@web3auth/auth-adapter";
import { ethers } from 'ethers';
import { SimpleAccountAPI } from "@account-abstraction/sdk";
import { HttpRpcClient } from "@account-abstraction/sdk/dist/src/HttpRpcClient";

interface Web3ContextType {
    account: string | null;
    btcAccount: string | null;
    balances: { eth: string; ftk: string; btc: string; usdt: string };
    loading: boolean;
    login: () => Promise<string | void>;
    logout: () => Promise<void>;
    refreshBalances: () => Promise<void>;
    transferFTK: (toAddress: string, amount: string) => Promise<string | null>;
    transferETH: (toAddress: string, amount: string) => Promise<string | null>;
    transferERC20: (contractAddress: string, toAddress: string, amount: string, decimals?: number) => Promise<string | null>;
    transferBTC: (toAddress: string, amount: string) => Promise<string | null>;
    signTransaction: (message: string) => Promise<string | null>;
    scAPI: any;
    web3authObj: any;
    finishOnboardingLogin: (formData: any, userInfo: any) => Promise<string | null>;
    walletUser: any | null;
    setWalletUser: (user: any) => void;
    b2bEntity: any | null;
    refreshWalletUser: () => Promise<void>;
    refreshB2BEntity: (address: string) => Promise<void>;
    finishSumsubOnboarding: (userInfo: any) => Promise<string | null>;
}

// Helper to hexify all fields for JSON-RPC compatibility (especially for Pimlico/Bundlers)
const deepHexlify = (obj: any): any => {
    if (typeof obj !== 'object' || obj === null) return obj;
    if (ethers.BigNumber.isBigNumber(obj)) return obj.toHexString().replace(/^0x0+(?!$)/, '0x');
    if (Array.isArray(obj)) return obj.map(deepHexlify);
    return Object.keys(obj).reduce((acc, key) => {
        acc[key] = deepHexlify(obj[key]);
        return acc;
    }, {} as any);
};

const Web3Context = createContext<Web3ContextType>({
    account: null,
    btcAccount: null,
    balances: { eth: "0", ftk: "0", btc: "0", usdt: "0" },
    loading: true,
    login: async () => { },
    logout: async () => { },
    refreshBalances: async () => { },
    transferFTK: async () => null,
    transferETH: async () => null,
    transferERC20: async () => null,
    transferBTC: async () => null,
    signTransaction: async () => null,
    scAPI: null,
    web3authObj: null,
    finishOnboardingLogin: async () => null,
    walletUser: null,
    setWalletUser: () => { },
    b2bEntity: null,
    refreshWalletUser: async () => { },
    refreshB2BEntity: async () => { },
    finishSumsubOnboarding: async () => null
});

const clientId = process.env.NEXT_PUBLIC_WEB3AUTH_CLIENT_ID || "";
const bundlerUrl = process.env.NEXT_PUBLIC_PIMLICO_BUNDLER_URL || "";
const pimlicoApiKey = process.env.NEXT_PUBLIC_PIMLICO_API_KEY || "";
const entryPoint = process.env.NEXT_PUBLIC_ERC4337_ENTRYPOINT_ADDRESS || "";
const factoryAddress = process.env.NEXT_PUBLIC_ERC4337_FACTORY_ADDRESS || "";

// Fallback RPC URL since Web3Auth requires a valid rpcTarget to initialize. Using Infura to avoid CORS blocking.
const rpcUrl = process.env.NEXT_PUBLIC_INFURA_RPC_URL ? process.env.NEXT_PUBLIC_INFURA_RPC_URL.replace("mainnet", "sepolia") : (process.env.NEXT_PUBLIC_DEFAULT_RPC_URL || process.env.NEXT_PUBLIC_RPC_URL || "");

const getRobustProvider = () => {
    const fallbackRpcUrl = process.env.NEXT_PUBLIC_FALLBACK_RPC_URL || rpcUrl;
    return new ethers.providers.FallbackProvider([
        { provider: new ethers.providers.StaticJsonRpcProvider(rpcUrl, 11155111), priority: 1, weight: 1 },
        { provider: new ethers.providers.StaticJsonRpcProvider(fallbackRpcUrl, 11155111), priority: 2, weight: 1 }
    ], 1);
};

const chainConfig = {
    chainNamespace: CHAIN_NAMESPACES.EIP155,
    chainId: "0xaa36a7",
    rpcTarget: rpcUrl,
    displayName: "Ethereum Sepolia",
    blockExplorerUrl: process.env.NEXT_PUBLIC_BLOCK_EXPLORER_URL || "",
    ticker: "ETH",
    tickerName: "Ethereum",
};

import { useCorporate } from './CorporateContext';
import { useRouter } from 'next/navigation';

export const Web3Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const router = useRouter();
    const { config: corporateConfig } = useCorporate();
    const [web3auth, setWeb3auth] = useState<Web3AuthNoModal | null>(null);
    const [scAPI, setScAPI] = useState<SimpleAccountAPI | null>(null);
    const [account, setAccount] = useState<string | null>(null);
    const [btcAccount, setBtcAccount] = useState<string | null>(null);
    const [balances, setBalances] = useState({ eth: "0", ftk: "0", btc: "0", usdt: "0" });
    const [walletUser, setWalletUser] = useState<any | null>(null);
    const [b2bEntity, setB2bEntity] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [accountIds, setAccountIds] = useState<string[]>([]); // Extracted from BFF for Secure Sockets

    const FTK_CONTRACT_ADDRESS = corporateConfig?.providers?.contracts?.FTK || process.env.NEXT_PUBLIC_FTK_CONTRACT || "";
    const USDT_CONTRACT_ADDRESS = corporateConfig?.providers?.contracts?.USDT || process.env.NEXT_PUBLIC_USDT_CONTRACT_ADDRESS || "";

    const setupSmartAccount = async (web3authProvider: IProvider, skipCookie = false) => {
        const ethersProvider = new ethers.providers.Web3Provider(web3authProvider as any);
        const signer = ethersProvider.getSigner();

        let saltIndex = ethers.BigNumber.from(0);
        const LEGACY_UNSALTED_CORP_ID = process.env.NEXT_PUBLIC_LEGACY_UNSALTED_CORP_ID || "";
        const isLegacy = !corporateConfig || corporateConfig.id === LEGACY_UNSALTED_CORP_ID;

        if (!isLegacy) {
            const tenantString = String(corporateConfig.id);
            const saltHash = ethers.utils.id(tenantString);
            saltIndex = ethers.BigNumber.from(ethers.utils.hexDataSlice(saltHash, 0, 8));
        }

        const api = new SimpleAccountAPI({
            provider: ethersProvider as any,
            entryPointAddress: entryPoint,
            factoryAddress: factoryAddress,
            owner: signer as any,
            index: saltIndex,
            paymasterAPI: {
                getPaymasterAndData: async (userOp: any) => {
                    try {
                        // CRITICAL: Resolve all properties (Promises) before hexifying
                        const resolvedOp = await ethers.utils.resolveProperties(userOp);
                        const hexifiedOp = deepHexlify(resolvedOp);

                        const response = await fetch(bundlerUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                jsonrpc: '2.0',
                                id: 1,
                                method: 'pm_sponsorUserOperation',
                                params: [hexifiedOp, entryPoint]
                            })
                        });
                        const res = await response.json();
                        if (res.error) {
                            console.error("Paymaster RPC Error:", JSON.stringify(res.error));
                            return "0x";
                        }
                        return res.result.paymasterAndData || "0x";
                    } catch (e) {
                        console.error("Paymaster Fetch Error:", e);
                        return "0x";
                    }
                }
            }
        });

        let address;
        try {
            address = await api.getAccountAddress();
            setScAPI(api);
            setAccount(address);
            console.log("Smart Account Activa (ERC-4337):", address);
        } catch (error: any) {
            console.error("Critical Error fetching Smart Account Address:", error);
            // Si falla el fetching de la dirección (revert), no podemos seguir con la cuenta
            setLoading(false);
            return { address: null, btcAddress: null };
        }

        // Intentar cargar entidad B2B si estamos en flujo merchant
        if (address) {
            await fetchB2BEntity(address);
        }

        let finalBtcAddress = null;
        // Derive BTC Address - Lazy-load WASM-based libraries to avoid SSR/build crash
        try {
            const [bitcoin, { default: ECPairFactory }, tinysecp] = await Promise.all([
                import('bitcoinjs-lib'),
                import('ecpair'),
                import('tiny-secp256k1')
            ]);
            const ECPair = ECPairFactory(tinysecp);

            // Some configurations or states might not support exporting the private key, 
            // returning {} or throwing. We handle this to prevent app crash.
            const privateKey = await web3authProvider.request({ method: "eth_private_key" }).catch(() => null) as string | null;

            if (privateKey && typeof privateKey === 'string') {
                const tenantBtcString = String(corporateConfig?.id || 'default_tenant');
                const isLegacyBTC = !corporateConfig || corporateConfig.providers?.legacy_unsalted_derivation === true || (Boolean(process.env.NEXT_PUBLIC_LEGACY_UNSALTED_CORP_ID) && corporateConfig.id === process.env.NEXT_PUBLIC_LEGACY_UNSALTED_CORP_ID);

                let finalPrivateKeyBuffer: Buffer;
                
                if (isLegacyBTC) {
                    finalPrivateKeyBuffer = Buffer.from(privateKey.padStart(64, '0'), 'hex');
                } else {
                    const isolatedBtcHash = ethers.utils.solidityKeccak256(
                        ['bytes32', 'string'],
                        [`0x${privateKey.padStart(64, '0')}`, tenantBtcString]
                    );
                    finalPrivateKeyBuffer = Buffer.from(isolatedBtcHash.replace('0x', ''), 'hex');
                }

                const keyPair = ECPair.fromPrivateKey(finalPrivateKeyBuffer, { network: bitcoin.networks.testnet });
                const { address: btcAddress } = bitcoin.payments.p2wpkh({ pubkey: keyPair.publicKey, network: bitcoin.networks.testnet });
                if (btcAddress) {
                    finalBtcAddress = btcAddress;
                    setBtcAccount(btcAddress);
                    console.log("BTC Testnet Account Generada:", btcAddress);
                }
            } else {
                console.warn("web3authProvider does not support eth_private_key or returned null/empty.");
            }
        } catch (error) {
            console.error("Error generating BTC account:", error);
        }

        // Sync with server cookie for Next.js Middleware Protection
        if (!skipCookie) {
            try {
                let currentVerifierId = null;
                let currentEmail = null;
                if (web3auth) {
                    const userInfo = await web3auth.getUserInfo().catch(() => null);
                    currentVerifierId = userInfo?.verifierId || userInfo?.idToken || userInfo?.email;
                    currentEmail = userInfo?.email || null;
                }

                const sessionRes = await fetch('/api/auth/wallet-session', {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'x-corporate-id': corporateConfig?.id || ''
                    },
                    body: JSON.stringify({ address, firebaseUid: currentVerifierId || address, email: currentEmail })
                });
                const sessionData = await sessionRes.json().catch(() => null);
                if (sessionData?.token && typeof window !== 'undefined') {
                    sessionStorage.setItem('wallet_onboarding_jwt', sessionData.token);
                }
            } catch (e) {
                console.error("Error setting wallet session cookie", e);
            }
        }

        return { address, btcAddress: finalBtcAddress };
    };

    const fetchB2BEntity = useCallback(async (address: string) => {
        try {
            console.log("Sincronizando Entidad B2B por dirección:", address);
            const { data, error } = await supabase
                .from('b2b_entities')
                .select('*')
                .eq('blockchain_address', address)
                .maybeSingle();

            if (!error && data) {
                setB2bEntity(data);
                console.log("Entidad B2B cargada:", data.business_name);
            }
        } catch (error) {
            console.error("Error fetching B2B entity:", error);
        }
    }, []);

    const fetchWalletUser = useCallback(async (firebaseUid: string, email?: string, currentAddress?: string | null) => {
        try {
            const res = await fetch('/api/wallet/check-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-corporate-id': corporateConfig?.id || ''
                },
                body: JSON.stringify({
                    firebaseUid,
                    email,
                    address: currentAddress || account || undefined
                })
            });
            if (!res.ok) return;
            const data = await res.json();
            if (data.token && typeof window !== 'undefined') {
                sessionStorage.setItem('wallet_onboarding_jwt', data.token);
            }
            if (data.user) {
                setWalletUser(data.user);
                console.log("Wallet User Profile cargado:", data.user.first_name);
            }
        } catch (error) {
            console.error("Error fetching wallet user profile:", error);
        }
    }, [corporateConfig?.id, account]);

    const refreshWalletUser = useCallback(async () => {
        const userInfo = await web3auth?.getUserInfo().catch(() => null);
        const verifierId = userInfo?.verifierId || userInfo?.idToken || userInfo?.email || account;
        if (verifierId) {
            await fetchWalletUser(verifierId, userInfo?.email, account);
        }
    }, [web3auth, fetchWalletUser, account]);

    const init = useCallback(async () => {
        try {
            const privateKeyProvider = new EthereumPrivateKeyProvider({ config: { chainConfig } });
            const web3auth = new Web3AuthNoModal({
                clientId,
                web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_DEVNET,
                privateKeyProvider,
            });

            const authAdapter = new AuthAdapter({
                adapterSettings: {
                    uxMode: "popup",
                },
            });
            web3auth.configureAdapter(authAdapter);
            setWeb3auth(web3auth);

            await web3auth.init();

            if (web3auth.connected && web3auth.provider) {
                // Durante el init inicial, NO asumas que el usuario está registrado.
                // Llamamos a setup con skipCookie=true por defecto en init.
                // El login real o el onboarding se encargarán de poner la cookie.
                const { address: derivedAddress } = await setupSmartAccount(web3auth.provider, true);

                const userInfo = await web3auth.getUserInfo().catch(() => null);
                const verifierId = userInfo?.verifierId || userInfo?.idToken || userInfo?.email || derivedAddress;
                if (verifierId) {
                    await fetchWalletUser(verifierId, userInfo?.email, derivedAddress);
                }
            }
        } catch (error) {
            console.warn("AA Init Notice:", error);
        } finally {
            setLoading(false);
        }
    }, [fetchWalletUser]);

    useEffect(() => {
        init();
    }, [init]);

    const login = async () => {
        console.log("=== INICIANDO LOGIN ===");
        console.log("web3auth status:", web3auth?.status);

        if (!web3auth) return;
        setLoading(true);

        try {
            // Check if we are really connected. Sometimes provider exist but status is not CONNECTED
            if (web3auth.status !== ADAPTER_STATUS.CONNECTED) {
                console.log("No conectado. Intentando connectTo...");
                await web3auth.connectTo(WALLET_ADAPTERS.AUTH, {
                    loginProvider: "google",
                });
            } else {
                console.log("Estado CONNECTED detectado, verificando integridad...");
            }

            const web3authProvider = web3auth.provider;
            if (!web3authProvider) {
                throw new Error("Conexión establecida pero no hay provider. Reintenta.");
            }

            console.log("Chequeando si el usuario está registrado...");
            // Use a defensive try/catch for getUserInfo
            const userInfo = await web3auth.getUserInfo().catch(async (err) => {
                console.warn("getUserInfo falló en estado conectado. Intentando reconexión forzada...", err);
                if (err.message?.includes("not connected")) {
                    await web3auth.logout();
                    await web3auth.connectTo(WALLET_ADAPTERS.AUTH, { loginProvider: "google" });
                    return await web3auth.getUserInfo();
                }
                throw err;
            });

            const verifierId = userInfo.verifierId || userInfo.idToken || userInfo.email;
            console.log("Usuario identificado:", verifierId?.substring(0, 10) + "...");

            const { address: derivedAddress } = await setupSmartAccount(web3authProvider, true);

            const res = await fetch('/api/wallet/check-user', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'x-corporate-id': corporateConfig?.id || ''
                },
                body: JSON.stringify({ 
                    firebaseUid: verifierId || derivedAddress, 
                    email: userInfo?.email, 
                    address: derivedAddress 
                })
            });
            const data = await res.json();
            if (data.token && typeof window !== 'undefined') {
                sessionStorage.setItem('wallet_onboarding_jwt', data.token);
            }
            
            // Evaluamos la completitud del Onboarding comprobando la versión legal del Tenant específico
            const requiredTncVersion = corporateConfig?.ui_theme?.legal?.version || "1.0";
            const isTncAccepted = data.membership?.accepted_tnc_version === requiredTncVersion || data.user?.accepted_tnc_version === requiredTncVersion;
            const hasBasicInfo = data.exists && data.user && isTncAccepted && data.user.first_name && !data.user.id_number?.startsWith('PENDING-') && data.user.kyc_status !== 'rejected';
            const compData = data.user?.compliance_data || {};
            const hasFullCompliance = 
                compData.pep !== undefined &&
                compData.fatca !== undefined &&
                compData.sujetoObligado !== undefined &&
                compData.estadoCivil &&
                compData.cuitCuil &&
                compData.actividadLaboral;

            const isFullyOnboarded = !!(hasBasicInfo && hasFullCompliance);

            if (isFullyOnboarded) {
                console.log("Usuario existente y completo, configurando cuenta...");
                await setupSmartAccount(web3authProvider, false); // Set session cookie
                setWalletUser(data.user);
                toast.success("Login Web3Auth & SCA Exitoso");
                return 'success';
            } else {
                console.log("Usuario incompleto o nuevo, redirigiendo a onboarding...");
                if (data.user) setWalletUser(data.user); // Keep partial data for wizard pre-fill
                router.push('/wallet/onboarding');
                return 'onboarding';
            }
        } catch (error) {
            console.error("=== ERROR EN LOGIN ===", error);
            const msg = (error as Error).message;
            toast.error("Error en el login social: " + msg);

            // Si el error es una inconsistencia, limpiamos para el siguiente intento
            if (msg.includes("not connected") || msg.includes("already connected")) {
                console.warn("Forzando logout por inconsistencia de estado...");
                await web3auth.logout().catch(() => { });
            }
        } finally {
            console.log("=== FIN LOGIN ===");
            setLoading(false);
        }
    };

    const refreshBalances = useCallback(async () => {
        if (!account) return;
        // MUST enforce tenant context mapping
        const entityId = corporateConfig?.id;
        
        try {
            // ZERO-MOCK & LEDGER PARITY:
            // Fetch exclusively from the backend BFF motor.
            // If the blockchain has funds but the backend hasn't reconciled them, we show 0 to the user.
            const res = await apiFetch(`/api/wallet/balance`, { headers: { 'x-corporate-id': entityId || '' } }, router);
            if (!res.ok) return;

            const data = await res.json();
            if (data.success && data.assetBalances) {
                setBalances({
                    eth: (data.assetBalances['ETH'] || 0).toString(),
                    ftk: (data.assetBalances['FTK'] || 0).toString(),
                    usdt: (data.assetBalances['USDT'] || data.assetBalances['USDC'] || 0).toString(),
                    btc: (data.assetBalances['BTC'] || 0).toString()
                });
            }

            if (data.accountIds && Array.isArray(data.accountIds)) {
                setAccountIds(prev => {
                    if (JSON.stringify(prev) === JSON.stringify(data.accountIds)) {
                        return prev; // Bail out of render. Keep stable reference.
                    }
                    return data.accountIds;
                });
            }
        } catch (error) {
            console.error("Error refreshing B2C unified balances:", error);
        }
    }, [account, corporateConfig?.id, router]);

    // Hook 1: The Fetcher (Runs on account change or visibility)
    useEffect(() => {
        if (account) refreshBalances();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && account) refreshBalances();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [account, corporateConfig?.id, refreshBalances]);

    // Hook 2: The Real-Time Socket (Runs strictly when accountIds exist)
    useEffect(() => {
        if (!account || accountIds.length === 0) return;

        const entityId = corporateConfig?.id || "default_tenant";
        let debounceTimer: NodeJS.Timeout;

        const debouncedFetch = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => refreshBalances(), 300);
        };

        const channel = supabase
            .channel(`b2c_txs_${entityId}_${account}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'yohub_ledger_entries',
                filter: `account_id=in.(${accountIds.join(',')})`
            }, debouncedFetch)
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'yohub_accounts',
                filter: `id=in.(${accountIds.join(',')})`
            }, debouncedFetch)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'wallet_transactions',
                filter: `to_address=eq.${account}`
            }, debouncedFetch)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'wallet_transactions',
                filter: `from_address=eq.${account}`
            }, debouncedFetch)
            .subscribe();

        return () => {
            clearTimeout(debounceTimer);
            supabase.removeChannel(channel);
        };
    }, [account, accountIds, corporateConfig?.id, refreshBalances]);

    const logout = async () => {
        if (!web3auth) return;

        try {
            if (web3auth.connected) {
                await web3auth.logout();
            }
        } catch (error) {
            console.error("Web3Auth logout error:", error);
        }

        setScAPI(null);
        setAccount(null);
        setBtcAccount(null);
        setBalances({ eth: "0", ftk: "0", btc: "0", usdt: "0" });
        setWalletUser(null);
        toast.success("Sesión cerrada");

        // Clear server cookie and redirect based on context
        try {
            await fetch('/api/auth/wallet-logout', { method: 'POST' });
        } finally {
            if (window.location.pathname.startsWith('/merchant')) {
                window.location.href = '/merchant/login';
            } else {
                window.location.href = '/wallet/welcome';
            }
        }
    };

    // Helper: espera el receipt real del bundler (polling)
    const waitForUserOpReceipt = async (userOpHash: string, maxAttempts = 30): Promise<string | null> => {
        for (let i = 0; i < maxAttempts; i++) {
            await new Promise(r => setTimeout(r, 3000)); // 3s between polls
            try {
                // Using bundler URL directly as it's the most reliable for AA receipts
                const result = await (await fetch(bundlerUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        jsonrpc: '2.0', id: 1,
                        method: 'eth_getUserOperationReceipt',
                        params: [userOpHash]
                    })
                })).json();

                if (result.result?.receipt) {
                    const success = result.result.success === true || result.result.success === 'true';
                    return success ? result.result.receipt.transactionHash : null;
                }
            } catch (e) {
                // Fallback attempt with normal RPC if bundler fetch fails
                try {
                    const result = await (await fetch(rpcUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            jsonrpc: '2.0', id: 1,
                            method: 'eth_getUserOperationReceipt',
                            params: [userOpHash]
                        })
                    })).json();

                    if (result.result?.receipt) {
                        const success = result.result.success === true || result.result.success === 'true';
                        return success ? result.result.receipt.transactionHash : null;
                    }
                } catch (_) { /* continue polling */ }
            }
        }
        return null;
    };

    /**
     * UNIFIED GASLESS TRANSACTION SENDER
     * Centralizes sponsorship logic and broadcasting for Account Abstraction.
     */
    const sendGaslessTransaction = async (target: string, data: string, value = "0", loadingMessage = "Procesando operación segura...") => {
        if (!scAPI) throw new Error("Wallet no inicializada");

            const notifyLoading = toast.loading(loadingMessage);
        try {
            const op = await scAPI.createUnsignedUserOp({
                target,
                data,
                value: ethers.BigNumber.isBigNumber(value) ? value : ethers.utils.parseEther(value || "0")
            });

            // Zero-Mock: Generate realistic dynamic signature length for gas profiling instead of a hardcoded risk string
            const simulationPayload = ethers.utils.arrayify(ethers.utils.id("gas-simulation"));
            op.signature = await (scAPI.owner as any).signMessage(simulationPayload);

            const resolvedOp = await ethers.utils.resolveProperties(op);
            const hexifiedOp = deepHexlify(resolvedOp);

            // Fetch sponsorship from AA Paymaster
            const sponsorResponse = await fetch(bundlerUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    jsonrpc: '2.0',
                    id: 1,
                    method: 'pm_sponsorUserOperation',
                    params: [hexifiedOp, entryPoint]
                })
            }).then(r => r.json());

            if (!sponsorResponse.result) {
                console.error("Paymaster Error:", sponsorResponse.error);
                throw new Error("Sponsorship failed: " + (sponsorResponse.error?.message || "Servicio no disponible"));
            }

            // Apply sponsorship data
            op.paymasterAndData = sponsorResponse.result.paymasterAndData;
            op.preVerificationGas = sponsorResponse.result.preVerificationGas;
            op.verificationGasLimit = sponsorResponse.result.verificationGasLimit;
            op.callGasLimit = sponsorResponse.result.callGasLimit;

            // Sign UserOp before sending to BFF
            const signedOp = await scAPI.signUserOp(op);

            // LA TRANSMISIÓN AHORA ES EXCLUSIVA POR BFF (Nunca saltarse el Backend)
            const entityId = corporateConfig?.id || "";
            const relayRes = await apiFetch('/api/wallet/transfer/broadcast', {
                method: 'POST',
                headers: { 
                    'x-corporate-id': entityId, 
                    'Content-Type': 'application/json' 
                },
                body: JSON.stringify({ 
                    signedOp, 
                    currency: data === "0x" ? "ETH" : "ERC20", 
                    amount: value?.toString() || "0"
                })
            }, router);

            const relayData = await relayRes.json();
            
            if (!relayData.success) {
                throw new Error("Transacción vetada por BFF: " + relayData.error);
            }
            
            const userOpHash = relayData.userOpHash;

            toast.dismiss(notifyLoading);
            const waitingToast = toast.loading('Confirmando en Blockchain...');

            const txHash = await waitForUserOpReceipt(userOpHash);
            toast.dismiss(waitingToast);

            if (txHash) {
                toast.success("Transacción Exitosa");
                refreshBalances();
                return txHash;
            } else {
                throw new Error("La transacción falló on-chain");
            }
        } catch (error: any) {
            toast.dismiss(notifyLoading);
            throw error;
        }
    };

    const transferFTK = async (toAddress: string, amount: string): Promise<string | null> => {
        try {
            const amountWei = ethers.utils.parseEther(amount.replace(/,/g, '.').trim());
            const data = new ethers.utils.Interface([
                "function transfer(address to, uint256 amount)"
            ]).encodeFunctionData("transfer", [toAddress.trim(), amountWei]);

            return await sendGaslessTransaction(FTK_CONTRACT_ADDRESS, data, "0", "Enviando FTK (Gasless)...");
        } catch (error: any) {
            console.error("FTK Error:", error);
            toast.error(error.message);
            return null;
        }
    };

    const transferERC20 = async (contractAddress: string, toAddress: string, amount: string, decimals = 18): Promise<string | null> => {
        try {
            const amountWei = ethers.utils.parseUnits(amount.replace(/,/g, '.').trim(), decimals);
            const data = new ethers.utils.Interface([
                "function transfer(address to, uint256 amount)"
            ]).encodeFunctionData("transfer", [toAddress.trim(), amountWei]);

            return await sendGaslessTransaction(contractAddress, data, "0", "Transfiriendo Asset (Gasless)...");
        } catch (error: any) {
            console.error("ERC20 Error:", error);
            toast.error(error.message);
            return null;
        }
    };

    const transferBTC = async (toAddress: string, amount: string): Promise<string | null> => {
        try {
            // Native BTC L1 cannot be sent via EVM Account Abstraction Paymasters.
            // Requires mapping to Omnibus backend payout API instead of direct smart contract execution.
            throw new Error("Transacciones L1 Bitcoin no soportadas vía Smart Contract (EVM). Utilice WBTC u Omnibus Payout.");
        } catch (error: any) {
            console.error("BTC Transfer Error:", error);
            toast.error(error.message);
            return null;
        }
    };

    const transferETH = async (toAddress: string, amount: string): Promise<string | null> => {
        try {
            const ethValue = amount.replace(/,/g, '.').trim();
            return await sendGaslessTransaction(toAddress.trim(), "0x", ethValue, "Enviando ETH (Gasless)...");
        } catch (error: any) {
            console.error("ETH Error:", error);
            toast.error(error.message);
            return null;
        }
    };

    const signTransaction = async (message: string) => {
        if (!scAPI) return null;
        try {
            return await (scAPI.owner as any).signMessage(message);
        } catch (error) {
            return null;
        }
    };

    const finishOnboardingLogin = async (formData: any, userInfo: any) => {
        if (!web3auth || !web3auth.provider) return "Sesión expirada.";
        try {
            const { address: ethAddress, btcAddress } = await setupSmartAccount(web3auth.provider);
            const res = await fetch('/api/wallet/onboarding', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userInfo.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    idNumber: formData.idNumber,
                    phone: formData.phone,
                    countryCode: formData.countryCode,
                    firebaseUid: userInfo.uid || userInfo.verifierId || userInfo.email,
                    blockchainAddress: ethAddress,
                    btcAddress: btcAddress
                })
            });
            const data = await res.json();
            if (!data.success) return data.error || "Error al registrar en BD";

            // CIERRE EXITOSO: Seteamos la cookie de sesión ahora que tenemos el perfil
            await setupSmartAccount(web3auth.provider, false);

            // Cargar perfil en estado local
            if (data.user) setWalletUser(data.user);

            return null; // Éxito
        } catch (e: any) {
            return e.message;
        }
    };

    const finishSumsubOnboarding = async (userInfo: any) => {
        if (!web3auth || !web3auth.provider) return "Sesión expirada.";
        try {
            const { address: ethAddress, btcAddress } = await setupSmartAccount(web3auth.provider);
            const res = await fetch('/api/wallet/onboarding/finalize', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userInfo.email,
                    firebaseUid: userInfo.uid || userInfo.verifierId || userInfo.email,
                    blockchainAddress: ethAddress,
                    btcAddress: btcAddress
                })
            });
            const data = await res.json();
            if (!data.success) return data.error || "Error al registrar validación en BD";

            // CIERRE EXITOSO: Seteamos la cookie de sesión ahora que tenemos el perfil
            await setupSmartAccount(web3auth.provider, false);

            // Cargar perfil en estado local
            if (data.user) setWalletUser(data.user);

            return null; // Éxito
        } catch (e: any) {
            return e.message;
        }
    };

    return (
        <Web3Context.Provider value={{
            account,
            btcAccount,
            balances,
            loading,
            login,
            logout,
            refreshBalances,
            transferFTK,
            transferETH,
            transferERC20,
            transferBTC,
            signTransaction,
            scAPI,
            web3authObj: web3auth,
            finishOnboardingLogin,
            walletUser,
            setWalletUser,
            b2bEntity,
            refreshWalletUser,
            refreshB2BEntity: fetchB2BEntity,
            finishSumsubOnboarding
        }}>
            {children}
        </Web3Context.Provider>
    );
};

export const useWeb3 = () => useContext(Web3Context);
